import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Feature, Map as OlMap } from "ol";
import { Geometry, Point } from "ol/geom";
import { Style, Fill, Stroke, Circle as CircleStyle } from "ol/style";
import { fromLonLat } from "ol/proj";
import { FeatureLike } from "ol/Feature";
import { Equipment, EquipmentUtils } from "src/app/bo/equipment";
import { EquipmentPosition } from "src/app/bo/hiboo/equipmentPosition";
import { HttpService } from "src/app/services/http.service";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { ShiftEnum, ShiftUtils, Shift } from "src/app/bo/shifts/shift";
import { finalize } from "rxjs/operators";
import { LoggerService } from "src/app/services/logger.service";
import { EquipmentRouteLayer } from "./EquipmentRouteLayer";
import { EquipmentRouteDto } from "src/app/bo/equipment/equipmentRoute";

const HIBOO_EQUIPMENTS_POSITIONS_ENDPOINT = 'Hiboo/GetEquipmentsPositions';

export class BlastEquipmentLayer {
    private readonly source: VectorSource<Feature<Geometry>>;
    private readonly map: OlMap;
    private equipments: Equipment[] = [];
    private equipmentsPositions: EquipmentPosition[] = [];
    private shifts: Shift[] = [];
    private siteId: number;
    private readonly routeLayer: EquipmentRouteLayer;
    private selectedEquipmentIds: number[] = []; // IDs des équipements sélectionnés
    private savedRoutes: EquipmentRouteDto[] = []; // Routes sauvegardées en BDD

    public layer: VectorLayer<VectorSource<Feature<Geometry>>>;
    public shouldLoadSavedPositions = false;
    public dataLoaded = new Subject<void>();
    public dataLoading = new Subject<boolean>();
    public shiftsLoaded = new Subject<void>();


    constructor(map: OlMap, private readonly http: HttpService, private readonly translate: TranslateService, private readonly logger: LoggerService, zIndex: number, siteId?: number) {
        this.map = map;
        this.siteId = siteId;
        this.source = new VectorSource();
        this.layer = new VectorLayer({
            source: this.source,
            style: (feature) => this.equipmentStyleFunction(feature),
        });

        this.layer.setZIndex(zIndex);
        this.map.addLayer(this.layer);

        // Initialiser la couche de routes avec un zIndex légèrement inférieur
        this.routeLayer = new EquipmentRouteLayer(map, zIndex - 1);

        this.loadAndApplySavedPositions();
    }

    public clear() {
        this.source.clear();
        this.routeLayer.clear();
    }

    public setEquipments(equipments: Equipment[]) {
        this.equipments = equipments.map((equipment) => ({
            ...equipment,
            displayName:
                EquipmentUtils.formatEquipmentDisplayName(equipment) +
                " - " +
                EquipmentUtils.getEquipmentTypeFromEquipment(equipment),
        }));
    }

    public fetchEquipments(siteId: number) {
        this.http
            .get<Equipment[]>(`Hiboo/${siteId}/GetMobileEquipments`)
            .subscribe((equipments) => {
                this.setEquipments(equipments);
                this.dataLoaded.next();
            });
            this.fetchShifts(siteId);
    }

    public fetchShifts(siteId: number, callback?: () => void) {
        this.http
            .get<Shift[]>(`Blasts/${siteId}/shifts`)
            .subscribe(
                (shifts) => {
                    this.shifts = shifts; // Garder tous les shifts, même ceux supprimés

                    this.shiftsLoaded.next();
                    
                    this.forceStyleRefresh();
                    
                    if (callback) {
                        callback();
                    }
                },
                (error) => {
                    this.logger.logError(`Error fetching shifts: ${error}`);
                }
            );
    }

    public getEquipments(): Equipment[] {
        return this.equipments;
    }

    public getEquipmentPositions(params: {
        selectedEquipments: Equipment[];
        selectedDate: Date;
        selectedDateRange: Date[];
        selectedShift: ShiftEnum;
        timeSelection: string;
    }) {
        this.dataLoading.next(true);
        if (!params.selectedEquipments || params.selectedEquipments.length === 0) {
            this.clear();
            this.dataLoading.next(false);
            return;
        }

        const equipmentExternalIdList: number[] = params.selectedEquipments
            .filter((equipment) => equipment.externalId !== undefined)
            .map((equipment) => equipment.externalId!);

        if (equipmentExternalIdList.length === 0) {
            this.clear();
            this.dataLoading.next(false);
            return;
        }

        let startDate: Date;
        let endDate: Date;

        if (params.timeSelection === 'shift') {
            const dates = ShiftUtils.calculateShiftTimeRange(params.selectedDate, params.selectedShift);
            startDate = dates.startDate;
            endDate = dates.endDate;
        } else {
            if (!params.selectedDateRange || params.selectedDateRange.length < 2 || !params.selectedDateRange[0] || !params.selectedDateRange[1]) {
                this.dataLoading.next(false);
                return;
            }
            startDate = new Date(params.selectedDateRange[0]);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(params.selectedDateRange[1]);
            endDate.setHours(23, 59, 59, 999);
        }

        const queryParams = new URLSearchParams();
        equipmentExternalIdList.forEach(id => queryParams.append('equipmentExternalIdsList', id.toString()));
        queryParams.append('startDate', startDate.toISOString());
        queryParams.append('endDate', endDate.toISOString());

        const apiUrl = `${HIBOO_EQUIPMENTS_POSITIONS_ENDPOINT}?${queryParams}`;

        // Stocker les dates pour le filtrage côté front
        const filterStartDate = startDate;
        const filterEndDate = endDate;

        this.http
            .get<EquipmentPosition[]>(apiUrl)
            .pipe(finalize(() => this.dataLoading.next(false)))
            .subscribe(
                (response) => {
                    // Filtrer les positions côté front pour garantir qu'elles sont dans la plage demandée
                    const filteredPositions = response.filter(position => {
                        const posDate = new Date(position.date);
                        return posDate >= filterStartDate && posDate <= filterEndDate;
                    });
                    this.equipmentsPositions = filteredPositions;
                    this.addEquipmentPositions(filteredPositions);
                    this.saveEquipmentPositionsToLocalStorage();
                    this.dataLoading.next(false);
                    this.dataLoaded.next();
                },
                (error) => {
                    this.logger.logError(`Error fetching equipment positions:${error}`);
                    this.dataLoading.next(false);
                }
            );
    }

    public addEquipmentPositions(equipmentsPositions: EquipmentPosition[], skipShiftReload = false, showAssignedEquipments?: boolean) {
        this.clear();

        // Réinitialiser selectedEquipmentIds avec tous les équipements qui ont des positions
        const equipmentIdsWithPositions = new Set(equipmentsPositions.map(p => p.equipmentExternalId));
        this.selectedEquipmentIds = Array.from(equipmentIdsWithPositions);

        equipmentsPositions.forEach((position) => {
            let equipment = this.equipments.find(
                (e) => e.externalId === position.equipmentExternalId
            );

            // Si l'équipement n'est pas dans la liste, créer un équipement temporaire
            if (!equipment) {
                equipment = {
                    externalId: position.equipmentExternalId,
                    displayName: `Equipment ${position.equipmentExternalId}`,
                    name: `Equipment ${position.equipmentExternalId}`
                };
            }

            // Vérifier le filtre d'assignation si spécifié
            if (showAssignedEquipments !== undefined) {
                const isAssigned = this.isEquipmentAssignedInShift(position);
                if (isAssigned && !showAssignedEquipments) {
                    return; // Ne pas ajouter l'équipement assigné si le filtre est désactivé
                }
            }

            // Vérifier si l'équipement est sélectionné
            const isEquipmentSelected = this.selectedEquipmentIds.includes(position.equipmentExternalId);

            const equipmentType = this.getEquipmentType(position.equipmentExternalId);
            const coords = fromLonLat([position.longitude, position.latitude]);
            const feature = new Feature({
                geometry: new Point(coords),
                equipmentType: equipmentType,
                positionData: position,
                equipment: equipment,
            });
            feature.set("equipmentType", equipmentType);
            feature.set("positionData", position);
            feature.set("equipment", equipment);
            feature.set("equipmentSelected", isEquipmentSelected);
            this.source.addFeature(feature);
        });

        // Créer une map des équipements pour passer à la couche de routes
        const equipmentMap = new Map<number, any>();
        this.equipments.forEach(e => {
            if (e.externalId !== undefined) {
                equipmentMap.set(e.externalId, e);
            }
        });

        // Ajouter aussi les équipements des positions qui ne sont pas dans la liste (pour garantir que toutes les routes sont créées)
        equipmentsPositions.forEach(p => {
            if (!equipmentMap.has(p.equipmentExternalId)) {
                equipmentMap.set(p.equipmentExternalId, {
                    externalId: p.equipmentExternalId,
                    displayName: `Equipment ${p.equipmentExternalId}`
                });
            }
        });

        // Créer les routes entre les positions (en utilisant les routes sauvegardées si disponibles)
        this.routeLayer.createRoutesFromPositions(equipmentsPositions, equipmentMap, this.savedRoutes);

        if (!skipShiftReload && this.siteId && equipmentsPositions.length > 0) {
            this.fetchShifts(this.siteId);
        } else {
            if (this.shifts.length > 0) {
                setTimeout(() => {
                    this.forceStyleRefresh();
                }, 10);
            }
        }
    }

    // Couleurs très distinctes pour les équipements (identiques à EquipmentRouteLayer)
    private static readonly EQUIPMENT_COLORS = [
        'rgba(230, 25, 75, 0.9)',    // Rouge vif
        'rgba(60, 180, 75, 0.9)',    // Vert vif
        'rgba(0, 130, 200, 0.9)',    // Bleu vif
        'rgba(255, 225, 25, 0.9)',   // Jaune vif
        'rgba(145, 30, 180, 0.9)',   // Violet
        'rgba(245, 130, 48, 0.9)',   // Orange
        'rgba(70, 240, 240, 0.9)',   // Cyan
        'rgba(240, 50, 230, 0.9)',   // Magenta
        'rgba(188, 246, 12, 0.9)',   // Lime
        'rgba(250, 190, 190, 0.9)',  // Rose
        'rgba(0, 128, 128, 0.9)',    // Teal
        'rgba(230, 190, 255, 0.9)',  // Lavande
        'rgba(154, 99, 36, 0.9)',    // Marron
        'rgba(255, 250, 200, 0.9)',  // Beige
        'rgba(128, 0, 0, 0.9)',      // Bordeaux
        'rgba(170, 255, 195, 0.9)',  // Menthe
        'rgba(128, 128, 0, 0.9)',    // Olive
        'rgba(255, 215, 180, 0.9)',  // Pêche
        'rgba(0, 0, 128, 0.9)'       // Navy
    ];

    /**
     * Génère une couleur basée sur l'ID de l'équipement
     */
    private getDynamicEquipmentColor(equipmentId: number): string {
        return BlastEquipmentLayer.EQUIPMENT_COLORS[equipmentId % BlastEquipmentLayer.EQUIPMENT_COLORS.length];
    }

    /**
     * Crée le style de pastille pour un équipement
     */
    private createDotStyle(equipmentId: number, isAssigned: boolean, positionDate?: Date): Style | Style[] {
        // Récupérer la couleur depuis la route layer pour garantir la cohérence
        const routes = this.routeLayer.getRoutesForEquipment(equipmentId);
        let routeColor: string;

        // Si l'équipement a des routes, trouver la route qui contient cette position
        if (routes && routes.length > 0) {
            if (positionDate && routes.length > 1) {
                // Si on a plusieurs routes (après split), trouver celle qui contient cette position
                const matchingRoute = routes.find(route => {
                    return positionDate >= route.startTime && positionDate <= route.endTime;
                });
                routeColor = matchingRoute ? matchingRoute.color : routes[0].color;
            } else {
                // Sinon, utiliser la couleur de la première route
                routeColor = routes[0].color;
            }
        } else {
            // Pas de route encore créée, utiliser une couleur dynamique basée sur l'ID
            routeColor = this.getDynamicEquipmentColor(equipmentId);
        }

        if (isAssigned) {
            // Pastille avec bordure verte pour les équipements assignés
            return [
                new Style({
                    image: new CircleStyle({
                        radius: 8,
                        fill: new Fill({
                            color: routeColor
                        }),
                        stroke: new Stroke({
                            color: '#00ff00',
                            width: 2
                        })
                    })
                })
            ];
        } else {
            // Pastille simple sans bordure
            return new Style({
                image: new CircleStyle({
                    radius: 6,
                    fill: new Fill({
                        color: routeColor
                    }),
                    stroke: new Stroke({
                        color: 'rgba(0, 0, 0, 0.3)',
                        width: 1
                    })
                })
            });
        }
    }

    private equipmentStyleFunction(feature: FeatureLike): Style | Style[] | null {
        // Vérifier d'abord la visibilité (checkbox)
        const equipmentSelected = feature.get("equipmentSelected");
        const positionData = feature.get("positionData") as EquipmentPosition;

        if (equipmentSelected === false) {
            return null; // Masquer si checkbox décochée
        }

        const equipmentId = positionData?.equipmentExternalId;
        const isAssigned = this.isEquipmentAssignedInShift(positionData);
        const positionDate = positionData ? new Date(positionData.date) : undefined;

        // Créer le style de base
        const baseStyle = this.createDotStyle(equipmentId, isAssigned, positionDate);

        // Si le slider est actif, appliquer les modifications de style
        const isHighlighted = feature.get("isHighlighted");
        const isFuture = feature.get("isFuture");
        const isWithinRadius = feature.get("isWithinRadius");

        // Si hors du rayon de 10 minutes, appliquer une opacité très réduite
        if (isWithinRadius === false) {
            return this.createVeryFadedStyle(baseStyle);
        }

        if (isHighlighted === true) {
            // Position actuelle (la plus proche): taille agrandie 150%, opacité 100%
            return this.createLargeStyle(baseStyle);
        } else if (isFuture === true) {
            // Position future (pas encore atteinte): taille réduite 60%, opacité 50%
            return this.createSmallFadedStyle(baseStyle);
        } else if (isFuture === false) {
            // Position passée (déjà traversée): taille réduite 60%, opacité 100%
            return this.createSmallStyle(baseStyle);
        }

        // Pas de slider actif, style normal
        return baseStyle;
    }

    private getEquipmentType(equipmentExternalId: number): string {
        // Chercher l'équipement dans la liste pour obtenir son type
        const equipment = this.equipments.find(e => e.externalId === equipmentExternalId);
        if (equipment && equipment.type) {
            return equipment.type;
        }
        // Utiliser EquipmentUtils comme fallback
        return EquipmentUtils.getEquipmentType(equipmentExternalId);
    }

    private isEquipmentAssignedInShift(positionData: EquipmentPosition): boolean {
        if (!positionData || !this.shifts || this.shifts.length === 0) {
            return false;
        }

        const positionDate = new Date(positionData.date);
        const equipmentId = positionData.equipmentExternalId;
        

        for (const shift of this.shifts) {
            if (shift.isDeleted || !shift.equipementsIds || shift.equipementsIds.trim() === '') {
                continue;
            }

            const equipmentIds = shift.equipementsIds
                .split(';')
                .map(id => id.trim())
                .filter(id => id)
                .map(id => parseInt(id, 10))
                .filter(id => !isNaN(id)); 

            const isEquipmentInShift = equipmentIds.includes(equipmentId);

            if (!isEquipmentInShift) {
                continue;
            }

            let isWithinTimeRange = false;
            
            if (shift.time === ShiftEnum.NightToMorning) {
                const shiftDate = new Date(shift.date);
                const positionHour = positionDate.getHours(); // Utiliser heure locale comme dans assignEquipmentToBlast
                const positionDateOnly = new Date(positionDate.getFullYear(), positionDate.getMonth(), positionDate.getDate());
                const shiftDateOnly = new Date(shiftDate.getFullYear(), shiftDate.getMonth(), shiftDate.getDate());
                
                if (positionHour >= 22 && positionHour <= 23) {
                  // Pour 22h-23h locales, même date (début du shift de nuit)
                  isWithinTimeRange = positionDateOnly.getTime() === shiftDateOnly.getTime();
                }
                else if (positionHour >= 0 && positionHour < 6) {
                  // Pour 0h-6h locales, le shift appartient à la veille (-1)
                  const dayBeforePosition = new Date(positionDateOnly.getTime() - 24 * 60 * 60 * 1000);
                  isWithinTimeRange = dayBeforePosition.getTime() === shiftDateOnly.getTime();
                }
                else {
                  isWithinTimeRange = false;
                }

            } else {
                // Pour les autres shifts (6h-14h et 14h-22h), vérifier date ET heure
                const positionHour = positionDate.getHours();
                const positionDateOnly = new Date(positionDate.getFullYear(), positionDate.getMonth(), positionDate.getDate());
                const shiftDate = new Date(shift.date);
                const shiftDateOnly = new Date(shiftDate.getFullYear(), shiftDate.getMonth(), shiftDate.getDate());
                
                const isSameDate = positionDateOnly.getTime() === shiftDateOnly.getTime();
                
                // Vérifier que l'heure correspond au shift
                let isCorrectTimeRange = false;
                if (shift.time === ShiftEnum.MorningToAfternoon) {
                    isCorrectTimeRange = positionHour >= 6 && positionHour < 14;
                } else if (shift.time === ShiftEnum.AfternoonToEvening) {
                    isCorrectTimeRange = positionHour >= 14 && positionHour < 22;
                }
                
                isWithinTimeRange = isSameDate && isCorrectTimeRange;
            }

            if (isEquipmentInShift && isWithinTimeRange) {
                return true;
            }
        }

        return false;
    }

    private saveEquipmentPositionsToLocalStorage() {
        const serializedPositions = JSON.stringify(this.equipmentsPositions);
        localStorage.setItem('equipmentsPositions', serializedPositions);
        localStorage.setItem('shouldLoadSavedPositions', 'true');
    }

    public loadAndApplySavedPositions() {
        const serializedPositions = localStorage.getItem('equipmentsPositions');
        const shouldLoad = localStorage.getItem('shouldLoadSavedPositions') === 'true';

        if (shouldLoad && serializedPositions) {
            this.equipmentsPositions = JSON.parse(serializedPositions);
            this.addEquipmentPositions(this.equipmentsPositions);
            // Ne pas afficher les équipements et routes au chargement initial depuis localStorage
            this.layer.setVisible(false);
            this.routeLayer.setVisible(false);
        }
    }
    public clearPositions() {
        this.clear();
    }

    public setSiteId(siteId: number) {
        this.siteId = siteId;
        if (this.equipmentsPositions.length > 0 && this.source.getFeatures().length > 0) {
            this.fetchShifts(siteId);
        }
    }

    public getShifts(): Shift[] {
        return this.shifts;
    }

    public getSiteId(): number | undefined {
        return this.siteId;
    }

    public getEquipmentPositionsCount(): number {
        return this.equipmentsPositions?.length || 0;
    }

    public getAllPositions(): EquipmentPosition[] {
        return this.equipmentsPositions || [];
    }

    public getFeaturesCount(): number {
        return this.source?.getFeatures()?.length || 0;
    }

    public refreshShifts() {
        if (this.siteId) {
            this.fetchShifts(this.siteId);
        }
    }

    public updateStylesAfterShiftChange() {

        if (this.siteId) {
            this.fetchShifts(this.siteId);
        } else {
            this.forceStyleRefresh();
        }
    }

    public refreshAllDataAfterAssignment() {
        if (!this.siteId) {
            return;
        }

        this.http.get<Shift[]>(`Blasts/${this.siteId}/shifts`)
            .subscribe(
                (shifts) => {
                    this.shifts = shifts.filter(shift => !shift.isDeleted);
                    
                    this.addEquipmentPositions(this.equipmentsPositions, true);
                },
                (error) => {
                    this.logger.logError(`Error refreshing shifts after assignment: ${error}`);
                }
            );
    }


    public updateLocalShiftAndRefresh(shift: Shift, equipmentIds: number[], isAssignment: boolean) {
        if (isAssignment) {
            const existingShiftIndex = this.shifts.findIndex(s => s.shiftId === shift.shiftId);
            if (existingShiftIndex >= 0) {
                this.shifts[existingShiftIndex] = { ...shift };
            } else {
                this.shifts.push({ ...shift });
            }
        } else {
            this.shifts = this.shifts.filter(s => s.shiftId !== shift.shiftId);
        }
        
        const features = this.source.getFeatures();
        equipmentIds.forEach(equipmentId => {
            const targetFeature = features.find(feature => {
                const positionData = feature.get("positionData") as EquipmentPosition;
                return positionData && positionData.equipmentExternalId === equipmentId;
            });
            
            if (targetFeature) {
                const positionData = targetFeature.get("positionData") as EquipmentPosition;
                const positionDate = positionData ? new Date(positionData.date) : undefined;
                const newStyle = this.createDotStyle(equipmentId, isAssignment, positionDate);

                targetFeature.setStyle(newStyle);
                targetFeature.changed();
            }
        });
        
        this.source.changed();
        this.layer.changed();

    }

    public forceLoadShifts(callback?: () => void, siteId?: number) {
        const targetSiteId = siteId || this.siteId;
        if (targetSiteId) {
            this.fetchShifts(targetSiteId, callback);
        } else if (callback) {
            callback();
        }
    }

    public forceStyleRefresh() {
        const features = this.source.getFeatures();
        features.forEach((feature) => {
            const positionData = feature.get("positionData") as EquipmentPosition;

            if (positionData) {
                const equipmentId = positionData.equipmentExternalId;
                const isAssigned = this.isEquipmentAssignedInShift(positionData);
                const positionDate = new Date(positionData.date);
                const newStyle = this.createDotStyle(equipmentId, isAssigned, positionDate);

                feature.setStyle(newStyle);
                feature.changed();
            }
        });
        
        this.source.changed();
        this.layer.changed();
    }

    public forceRecreateFeatures() {
        const currentPositions = [...this.equipmentsPositions];

        this.clear();

        currentPositions.forEach((position) => {
            let equipment = this.equipments.find(
                (e) => e.externalId === position.equipmentExternalId
            );

            // Si l'équipement n'est pas dans la liste, créer un équipement temporaire
            if (!equipment) {
                equipment = {
                    externalId: position.equipmentExternalId,
                    displayName: `Equipment ${position.equipmentExternalId}`,
                    name: `Equipment ${position.equipmentExternalId}`
                };
            }

            // Vérifier si l'équipement est sélectionné
            const isEquipmentSelected = this.selectedEquipmentIds.includes(position.equipmentExternalId);

            const equipmentType = this.getEquipmentType(position.equipmentExternalId);
            const coords = fromLonLat([position.longitude, position.latitude]);

            const feature = new Feature({
                geometry: new Point(coords),
                equipmentType: equipmentType,
                positionData: position,
                equipment: equipment,
            });

            feature.set("equipmentType", equipmentType);
            feature.set("positionData", position);
            feature.set("equipment", equipment);
            feature.set("equipmentSelected", isEquipmentSelected);
            // Ne pas utiliser setStyle(), laisser equipmentStyleFunction() gérer le style

            this.source.addFeature(feature);

        });

    }

    public fixAllEquipmentStyles() {
        const features = this.source.getFeatures();
        
        features.forEach((feature) => {
            const positionData = feature.get("positionData") as EquipmentPosition;

            if (positionData) {
                const equipmentId = positionData.equipmentExternalId;
                const isAssigned = this.isEquipmentAssignedInShift(positionData);
                const positionDate = new Date(positionData.date);
                const correctStyle = this.createDotStyle(equipmentId, isAssigned, positionDate);

                feature.setStyle(correctStyle);
                feature.changed();
            }
        });
        
        this.source.changed();
        this.layer.changed();
    }


    public applyAssignmentStyleImmediately(equipmentIds: number[], isAssigned: boolean, showAssignedEquipments?: boolean) {
        this.addEquipmentPositions(this.equipmentsPositions, true, showAssignedEquipments);
    }

    /**
     * Met à jour les styles des équipements basé sur le filtre d'assignation
     */
    public updateStylesBasedOnAssignmentFilter(showAssignedEquipments: boolean) {
        // Recréer tous les équipements avec le nouveau filtre
        this.addEquipmentPositions(this.equipmentsPositions, true, showAssignedEquipments);

        // Forcer le rafraîchissement des styles après la recréation
        setTimeout(() => {
            this.forceStyleRefresh();
        }, 10);
    }

    /**
     * Active ou désactive l'affichage des routes entre les positions
     */
    public setRoutesVisible(visible: boolean) {
        this.routeLayer.setVisible(visible);
    }

    /**
     * Vérifie si les routes sont visibles
     */
    public areRoutesVisible(): boolean {
        return this.routeLayer.isVisible();
    }

    /**
     * Active ou désactive l'affichage des équipements
     */
    public setEquipmentsVisible(visible: boolean) {
        this.layer.setVisible(visible);
    }

    /**
     * Vérifie si les équipements sont visibles
     */
    public areEquipmentsVisible(): boolean {
        return this.layer.getVisible();
    }

    /**
     * Filtre les équipements visibles par leurs IDs
     */
    public filterEquipmentsByIds(selectedEquipmentIds: number[]) {
        // Stocker les IDs sélectionnés pour les utiliser dans filterPositionsByTime
        this.selectedEquipmentIds = selectedEquipmentIds;

        const features = this.source.getFeatures();
        features.forEach(feature => {
            const equipment = feature.get('equipment');
            if (equipment && equipment.externalId) {
                const isSelected = selectedEquipmentIds.includes(equipment.externalId);
                feature.set('equipmentSelected', isSelected);
                feature.setStyle(undefined); // Forcer le recalcul du style via equipmentStyleFunction
                feature.changed();
            }
        });

        // Forcer le refresh complet de la couche
        this.source.changed();
        this.layer.changed();

        // Filtrer aussi les routes
        if (this.routeLayer) {
            this.routeLayer.filterRoutesByEquipmentIds(selectedEquipmentIds);
        }
    }

    /**
     * Obtient la couche de routes
     */
    public getRouteLayer(): EquipmentRouteLayer {
        return this.routeLayer;
    }

    /**
     * Définit les routes sauvegardées à utiliser lors de la création des routes
     */
    public setSavedRoutes(routes: EquipmentRouteDto[]): void {
        this.savedRoutes = routes;
    }

    public addSavedRoutes(routes: EquipmentRouteDto[]): void {
        this.savedRoutes.push(...routes);
    }

    public removeSavedRoutes(routeIds: number[]): void {
        this.savedRoutes = this.savedRoutes.filter(r => r.id === undefined || !routeIds.includes(r.id));
    }

    // Rayon de temps en millisecondes (10 minutes)
    private static readonly TIME_RADIUS_MS = 10 * 60 * 1000;

    /**
     * Filtre les positions affichées en fonction d'un temps sélectionné
     * Les positions dans le rayon de 10 minutes sont mises en avant, les autres ont une opacité réduite
     */
    public filterPositionsByTime(filterDate: Date): void {
        if (!this.equipmentsPositions || this.equipmentsPositions.length === 0) {
            return;
        }

        // Effacer toutes les features actuelles
        this.source.clear();

        const filterTimeMs = filterDate.getTime();
        const radiusStart = filterTimeMs - BlastEquipmentLayer.TIME_RADIUS_MS;
        const radiusEnd = filterTimeMs + BlastEquipmentLayer.TIME_RADIUS_MS;

        // Pour chaque équipement, trouver la position la plus proche du temps sélectionné
        const equipmentClosestPositions = new Map<number, EquipmentPosition>();

        this.equipmentsPositions.forEach(position => {
            const equipmentId = position.equipmentExternalId;
            const positionDate = new Date(position.date);

            // Ne considérer que les positions antérieures ou égales au temps sélectionné pour le highlight
            if (positionDate <= filterDate) {
                const current = equipmentClosestPositions.get(equipmentId);
                if (!current) {
                    equipmentClosestPositions.set(equipmentId, position);
                } else {
                    const currentDate = new Date(current.date);
                    // Garder la position la plus proche (mais toujours <= filterDate)
                    if (positionDate > currentDate) {
                        equipmentClosestPositions.set(equipmentId, position);
                    }
                }
            }
        });

        // Afficher toutes les positions avec différents styles selon le rayon
        this.equipmentsPositions.forEach((position) => {
            let equipment = this.equipments.find(
                (e) => e.externalId === position.equipmentExternalId
            );

            if (!equipment) {
                equipment = {
                    externalId: position.equipmentExternalId,
                    displayName: `Equipment ${position.equipmentExternalId}`,
                    name: `Equipment ${position.equipmentExternalId}`
                };
            }

            // Vérifier si l'équipement est sélectionné
            const isEquipmentSelected = this.selectedEquipmentIds.includes(position.equipmentExternalId);

            const equipmentType = this.getEquipmentType(position.equipmentExternalId);
            const coords = fromLonLat([position.longitude, position.latitude]);
            const positionDate = new Date(position.date);
            const positionTimeMs = positionDate.getTime();

            // Vérifier si la position est dans le rayon de 10 minutes
            const isWithinRadius = positionTimeMs >= radiusStart && positionTimeMs <= radiusEnd;

            // Vérifier si c'est la position la plus proche du temps sélectionné
            const closestPosition = equipmentClosestPositions.get(position.equipmentExternalId);
            const isHighlighted = closestPosition === position;

            // Vérifier si la position est dans le futur (après le temps sélectionné)
            const isFuture = positionDate > filterDate;

            const feature = new Feature({
                geometry: new Point(coords),
                equipmentType: equipmentType,
                positionData: position,
                equipment: equipment,
            });

            feature.set("equipmentType", equipmentType);
            feature.set("positionData", position);
            feature.set("equipment", equipment);
            feature.set("isHighlighted", isHighlighted);
            feature.set("isFuture", isFuture);
            feature.set("equipmentSelected", isEquipmentSelected);
            feature.set("isWithinRadius", isWithinRadius); // Nouveau flag pour le rayon de 10 min

            this.source.addFeature(feature);
        });

        // Mettre à jour le temps de filtrage pour les routes
        this.routeLayer.setFilterTime(filterDate);
    }

    /**
     * Réduit l'opacité d'une couleur rgba de 50%
     */
    private fadeColor(color: string): string {
        const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
        if (match) {
            const r = match[1];
            const g = match[2];
            const b = match[3];
            const a = match[4] ? parseFloat(match[4]) : 1;
            return `rgba(${r}, ${g}, ${b}, ${a * 0.5})`;
        }
        return color;
    }

    /**
     * Crée un style avec taille agrandie (pour la position focus/highlight)
     */
    private createLargeStyle(baseStyle: Style | Style[]): Style | Style[] {
        if (Array.isArray(baseStyle)) {
            // Pour les styles assignés (pastille avec bordure)
            return baseStyle.map(style => {
                const image = style.getImage();

                if (image instanceof CircleStyle) {
                    // Agrandir la taille de la pastille à 150%
                    const fill = image.getFill();
                    const stroke = image.getStroke();
                    const currentRadius = image.getRadius();

                    return new Style({
                        image: new CircleStyle({
                            radius: currentRadius * 1.5, // Agrandir à 150%
                            fill: fill,
                            stroke: stroke
                        })
                    });
                }

                return style;
            });
        } else {
            // Pour les pastilles simples
            const image = baseStyle.getImage();

            if (image instanceof CircleStyle) {
                const fill = image.getFill();
                const stroke = image.getStroke();
                const currentRadius = image.getRadius();

                return new Style({
                    image: new CircleStyle({
                        radius: currentRadius * 1.5, // Agrandir à 150%
                        fill: fill,
                        stroke: stroke
                    })
                });
            }

            return baseStyle;
        }
    }

    /**
     * Crée un style avec taille réduite SEULEMENT (pour positions passées)
     */
    private createSmallStyle(baseStyle: Style | Style[]): Style | Style[] {
        if (Array.isArray(baseStyle)) {
            // Pour les styles assignés (pastille avec bordure)
            return baseStyle.map(style => {
                const image = style.getImage();

                if (image instanceof CircleStyle) {
                    // Réduire la taille de la pastille à 60%
                    const fill = image.getFill();
                    const stroke = image.getStroke();
                    const currentRadius = image.getRadius();

                    return new Style({
                        image: new CircleStyle({
                            radius: currentRadius * 0.6, // Réduire à 60%
                            fill: fill,
                            stroke: stroke
                        })
                    });
                }

                return style;
            });
        } else {
            // Pour les pastilles simples
            const image = baseStyle.getImage();

            if (image instanceof CircleStyle) {
                const fill = image.getFill();
                const stroke = image.getStroke();
                const currentRadius = image.getRadius();

                return new Style({
                    image: new CircleStyle({
                        radius: currentRadius * 0.6, // Réduire à 60%
                        fill: fill,
                        stroke: stroke
                    })
                });
            }

            return baseStyle;
        }
    }

    /**
     * Crée un style avec taille réduite ET opacité réduite (pour positions futures)
     */
    private createSmallFadedStyle(baseStyle: Style | Style[]): Style | Style[] {
        if (Array.isArray(baseStyle)) {
            // Pour les styles assignés (pastille avec bordure)
            return baseStyle.map(style => {
                const image = style.getImage();
                if (image instanceof CircleStyle) {
                    // Réduire la taille de la pastille à 60% ET réduire l'opacité
                    const fill = image.getFill();
                    const stroke = image.getStroke();
                    const currentRadius = image.getRadius();

                    // Créer une couleur avec opacité réduite pour le remplissage
                    const fillColor = fill?.getColor() as string;
                    const fadedFillColor = this.fadeColor(fillColor);

                    // Créer une couleur avec opacité réduite pour la bordure
                    const strokeColor = stroke?.getColor() as string;
                    const fadedStrokeColor = this.fadeColor(strokeColor);

                    return new Style({
                        image: new CircleStyle({
                            radius: currentRadius * 0.6, // Réduire à 60%
                            fill: new Fill({
                                color: fadedFillColor
                            }),
                            stroke: new Stroke({
                                color: fadedStrokeColor,
                                width: stroke?.getWidth() || 2
                            })
                        })
                    });
                }
                return style;
            });
        } else {
            // Pour les pastilles simples
            const image = baseStyle.getImage();
            if (image instanceof CircleStyle) {
                const fill = image.getFill();
                const stroke = image.getStroke();
                const currentRadius = image.getRadius();

                // Créer une couleur avec opacité réduite
                const fillColor = fill?.getColor() as string;
                const fadedFillColor = this.fadeColor(fillColor);

                const strokeColor = stroke?.getColor() as string;
                const fadedStrokeColor = this.fadeColor(strokeColor);

                return new Style({
                    image: new CircleStyle({
                        radius: currentRadius * 0.6, // Réduire à 60%
                        fill: new Fill({
                            color: fadedFillColor
                        }),
                        stroke: new Stroke({
                            color: fadedStrokeColor,
                            width: stroke?.getWidth() || 1
                        })
                    })
                });
            }
            return baseStyle;
        }
    }

    /**
     * Crée un style avec opacité très réduite (10%) pour les positions hors du rayon de 10 minutes
     */
    private createVeryFadedStyle(baseStyle: Style | Style[]): Style | Style[] {
        if (Array.isArray(baseStyle)) {
            return baseStyle.map(style => {
                const image = style.getImage();
                if (image instanceof CircleStyle) {
                    const fill = image.getFill();
                    const stroke = image.getStroke();
                    const currentRadius = image.getRadius();

                    const fillColor = fill?.getColor() as string;
                    const veryFadedFillColor = this.setColorOpacity(fillColor, 0.1);

                    const strokeColor = stroke?.getColor() as string;
                    const veryFadedStrokeColor = this.setColorOpacity(strokeColor, 0.1);

                    return new Style({
                        image: new CircleStyle({
                            radius: currentRadius * 0.4, // Réduire à 40%
                            fill: new Fill({
                                color: veryFadedFillColor
                            }),
                            stroke: new Stroke({
                                color: veryFadedStrokeColor,
                                width: 1
                            })
                        })
                    });
                }
                return style;
            });
        } else {
            const image = baseStyle.getImage();
            if (image instanceof CircleStyle) {
                const fill = image.getFill();
                const stroke = image.getStroke();
                const currentRadius = image.getRadius();

                const fillColor = fill?.getColor() as string;
                const veryFadedFillColor = this.setColorOpacity(fillColor, 0.1);

                const strokeColor = stroke?.getColor() as string;
                const veryFadedStrokeColor = this.setColorOpacity(strokeColor, 0.1);

                return new Style({
                    image: new CircleStyle({
                        radius: currentRadius * 0.4, // Réduire à 40%
                        fill: new Fill({
                            color: veryFadedFillColor
                        }),
                        stroke: new Stroke({
                            color: veryFadedStrokeColor,
                            width: 1
                        })
                    })
                });
            }
            return baseStyle;
        }
    }

    /**
     * Définit l'opacité d'une couleur rgba
     */
    private setColorOpacity(color: string, opacity: number): string {
        if (!color) return `rgba(128, 128, 128, ${opacity})`;
        const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
        if (match) {
            const r = match[1];
            const g = match[2];
            const b = match[3];
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
        return color;
    }
}
